import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import type { Agency, AgencyUpdateInput, BusinessHours } from '@aimmo/contracts';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Input,
  Label,
  PageHeader,
  RelationPicker,
  Skeleton,
  toast,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { invalidateActivityViews } from '../lib/activities.js';
import { useAbility } from '../lib/abilities.js';
import { resolveComune, searchComuni } from '../resources/geo-lookups.js';
import { SendingDomainCard } from '../features/email/sending-domain-card.js';
import { agencyQuery, queryKeys } from '../lib/queries.js';

const DAYS: { key: keyof BusinessHours; label: string }[] = [
  { key: 'mon', label: 'Lunedì' },
  { key: 'tue', label: 'Martedì' },
  { key: 'wed', label: 'Mercoledì' },
  { key: 'thu', label: 'Giovedì' },
  { key: 'fri', label: 'Venerdì' },
  { key: 'sat', label: 'Sabato' },
  { key: 'sun', label: 'Domenica' },
];

/** The zones an Italian agency plausibly sits in; anything else is typed. */
const TIMEZONES = ['Europe/Rome', 'Europe/Zurich', 'Europe/Malta', 'UTC'];

/**
 * The agency's own record (U7): who it is, and what its working week looks
 * like — which is what U5's reminder states are computed against.
 *
 * There is deliberately nothing about sessions, passwords or MFA here. Those
 * are platform constraints, identical for every tenant, and live in the realm.
 */
export function AgencySettingsPage() {
  const ability = useAbility();
  const canEdit = ability.can('update', 'Agency');

  const { data, isPending, isError, refetch } = useQuery(agencyQuery);

  if (isError) return <ErrorState onRetry={() => void refetch()} />;
  if (isPending) return <Skeleton className="h-64 w-full" />;

  // Keyed by the record: the form starts from it, and a refetch — the one
  // after this very save, or another tab's — does not overwrite what is being
  // typed, which is what copying it in from an effect used to do.
  return <AgencySettingsForm key={data.id} agency={data} canEdit={canEdit} />;
}

function formOf(agency: Agency): AgencyUpdateInput {
  return {
    name: agency.name,
    email: agency.email ?? '',
    phone: agency.phone ?? '',
    mobile: agency.mobile ?? '',
    addressStreet: agency.addressStreet ?? '',
    addressNumber: agency.addressNumber ?? '',
    addressPostalCode: agency.addressPostalCode ?? '',
    comuneId: agency.comuneId ?? '',
    timezone: agency.timezone,
    businessHours: agency.businessHours,
  };
}

function AgencySettingsForm({ agency, canEdit }: { agency: Agency; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [form, setForm] = React.useState<AgencyUpdateInput>(() => formOf(agency));

  const save = useMutation({
    mutationFn: async (input: AgencyUpdateInput) =>
      assertOk(await api.agency.update({ body: input })).body,
    onSuccess: async () => {
      toast.success('Dati agenzia aggiornati.');
      await queryClient.invalidateQueries({ queryKey: queryKeys.agency });
      // Reminder states are computed against these: the agenda has to re-ask.
      await invalidateActivityViews(queryClient);
    },
    onError: () => toast.error('Salvataggio non riuscito.'),
  });

  const patch = (values: Partial<AgencyUpdateInput>) => setForm({ ...form, ...values });

  const setInterval_ = (
    day: keyof BusinessHours,
    index: number,
    position: 0 | 1,
    value: string,
  ) => {
    const intervals = form.businessHours[day].map((interval, i): [string, string] =>
      i === index ? (position === 0 ? [value, interval[1]] : [interval[0], value]) : interval,
    );
    patch({ businessHours: { ...form.businessHours, [day]: intervals } });
  };

  return (
    <section className="grid gap-[var(--card-gap)]">
      <PageHeader title="Agenzia" description="Dati anagrafici, fuso orario e orari di lavoro." />

      <Card>
        <CardHeader>
          <CardTitle>Anagrafica</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="grid gap-1">
            <Label htmlFor="agency-name">Nome</Label>
            <Input
              id="agency-name"
              disabled={!canEdit}
              value={form.name}
              onChange={(event) => patch({ name: event.target.value })}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="agency-email">Email</Label>
            <Input
              id="agency-email"
              type="email"
              disabled={!canEdit}
              value={form.email ?? ''}
              onChange={(event) => patch({ email: event.target.value })}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="agency-phone">Telefono</Label>
            <Input
              id="agency-phone"
              disabled={!canEdit}
              value={form.phone ?? ''}
              onChange={(event) => patch({ phone: event.target.value })}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="agency-street">Via</Label>
            <Input
              id="agency-street"
              disabled={!canEdit}
              value={form.addressStreet ?? ''}
              onChange={(event) => patch({ addressStreet: event.target.value })}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="agency-number">Civico</Label>
            <Input
              id="agency-number"
              disabled={!canEdit}
              value={form.addressNumber ?? ''}
              onChange={(event) => patch({ addressNumber: event.target.value })}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="agency-cap">CAP</Label>
            <Input
              id="agency-cap"
              disabled={!canEdit}
              value={form.addressPostalCode ?? ''}
              onChange={(event) => patch({ addressPostalCode: event.target.value })}
            />
          </div>
          <div className="grid gap-1">
            <Label>Comune</Label>
            <RelationPicker
              value={form.comuneId || null}
              onChange={(value) => patch({ comuneId: value ?? '' })}
              search={searchComuni}
              resolve={resolveComune}
              aria-label="Comune"
              disabled={!canEdit}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Orologio</CardTitle>
          <CardDescription>
            Gli stati dei promemoria si calcolano su questo fuso e su queste ore.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid max-w-sm gap-1">
            <Label htmlFor="agency-timezone">Fuso orario</Label>
            <Input
              id="agency-timezone"
              list="agency-timezones"
              disabled={!canEdit}
              value={form.timezone}
              onChange={(event) => patch({ timezone: event.target.value })}
            />
            <datalist id="agency-timezones">
              {TIMEZONES.map((zone) => (
                <option key={zone} value={zone} />
              ))}
            </datalist>
          </div>

          <div className="grid gap-2">
            {DAYS.map((day) => (
              <div key={day.key} className="flex flex-wrap items-center gap-2">
                <span className="w-28 text-body text-ink-2">{day.label}</span>

                {form.businessHours[day.key].length === 0 && (
                  <span className="text-secondary text-muted">chiuso</span>
                )}

                {form.businessHours[day.key].map((interval, index) => (
                  <span key={index} className="flex items-center gap-1">
                    <Input
                      className="w-28"
                      type="time"
                      aria-label={`${day.label} inizio ${index + 1}`}
                      disabled={!canEdit}
                      value={interval[0]}
                      onChange={(event) => setInterval_(day.key, index, 0, event.target.value)}
                    />
                    <Input
                      className="w-28"
                      type="time"
                      aria-label={`${day.label} fine ${index + 1}`}
                      disabled={!canEdit}
                      value={interval[1]}
                      onChange={(event) => setInterval_(day.key, index, 1, event.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Rimuovi intervallo ${index + 1} di ${day.label}`}
                      disabled={!canEdit}
                      onClick={() =>
                        patch({
                          businessHours: {
                            ...form.businessHours,
                            [day.key]: form.businessHours[day.key].filter((_, i) => i !== index),
                          },
                        })
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </span>
                ))}

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!canEdit || form.businessHours[day.key].length >= 4}
                  onClick={() =>
                    patch({
                      businessHours: {
                        ...form.businessHours,
                        [day.key]: [
                          ...form.businessHours[day.key],
                          ['09:00', '13:00'] as [string, string],
                        ],
                      },
                    })
                  }
                >
                  <Plus />
                  Intervallo
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {canEdit && (
        <footer className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            disabled={save.isPending}
            onClick={() => save.mutate(form)}
          >
            Salva
          </Button>
        </footer>
      )}

      <SendingDomainCard
        canEdit={canEdit}
        api={{
          key: queryKeys.agencySendingDomain,
          get: async () => assertOk(await api.email.getSendingDomain()).body,
          set: async (body) => assertOk(await api.email.setSendingDomain({ body })).body,
          verify: async () => assertOk(await api.email.verifySendingDomain({ body: {} })).body,
          remove: async () => assertOk(await api.email.removeSendingDomain()).body,
        }}
      />
    </section>
  );
}
